import { darken, lighten, rgbToHex } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PaletteColor } from "@mui/material/styles/createPalette";

export type ColorSchema =
  | "primary"
  | "secondary"
  | "info"
  | "success"
  | "warning"
  | "error";

declare module "@mui/material/styles" {
  interface Palette {
    feature: PaletteColor;
  }

  interface PaletteOptions {
    feature?: PaletteColor;
  }
}

declare module "@mui/material/styles/createPalette" {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
}

const grey = {
  0: "#FFFFFF",
  100: "#FDFDFD",
  200: "#F0F0F0",
  300: "#EEEEEE",
  400: "#DDDDDD",
  500: "#919EAB",
  600: "#637381",
  700: "#323741",
  800: "#212B36",
  900: "#111D26",
};

const getColorPalette = (mainColor: string, contrastText: string) => ({
  lighter: rgbToHex(lighten(mainColor, 0.4)).toUpperCase(),
  light: rgbToHex(lighten(mainColor, 0.2)).toUpperCase(),
  main: mainColor,
  dark: rgbToHex(darken(mainColor, 0.2)).toUpperCase(),
  darker: rgbToHex(darken(mainColor, 0.4)).toUpperCase(),
  contrastText,
});

const primary = getColorPalette("#8d99ff", grey[100]);
const secondary = getColorPalette("#98D2C3", grey[100]);
const info = getColorPalette("#1A0D56", grey[100]);
const success = getColorPalette("#7BDB12", grey[100]);
const warning = getColorPalette("#fdd835", grey[100]);
const error = getColorPalette("#FF3333", grey[100]);
const feature = getColorPalette("#F2622E", grey[100]);

const common = {
  common: { black: "#000000", white: "#FFFFFF" },
  primary: primary,
  secondary: secondary,
  info: info,
  success: success,
  warning: warning,
  error: error,
  feature: feature,
  grey: grey,
  divider: grey[400],
  action: {
    hover: alpha("#8d99ff", 0.2),
    selected: alpha(grey[500], 0.16),
    disabled: alpha(grey[500], 0.8),
    disabledBackground: alpha(grey[500], 0.24),
    focus: alpha(grey[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export default function palette(themeMode: "light" | "dark") {
  const light = {
    ...common,
    mode: "light",
    text: {
      primary: grey[900],
      secondary: grey[600],
      disabled: grey[400],
    },
    background: { paper: grey[0], default: grey[200], neutral: grey[300] },
    action: {
      ...common.action,
      active: grey[900],
    },
  } as const;

  const dark = {
    ...common,
    mode: "dark",
    text: {
      primary: grey[100],
      secondary: grey[500],
      disabled: grey[600],
    },
    background: {
      paper: grey[800],
      default: grey[900],
      neutral: alpha(grey[500], 0.16),
    },
    action: {
      ...common.action,
      active: grey[500],
    },
  } as const;

  return themeMode === "light" ? light : dark;
}
